"use client"
import {
  fetchFromSkuIds,
  getRandom,
  getPDPUrl,
  getShortenedUrlwithGeneral,
  sanitizeText,
} from "@/utils/helper"
import Link from "next/link"
import React, { useEffect, useId, useRef, useState } from "react"
import { getConfig } from "@/constants/config"
import { useWindowSize } from "@kohler-dsm-library/kohler-core"
import Slider from "react-slick"
import DynamicImage from "@/components/core/Image/DynamicImage"
import { singleSliderSettings } from "@/components/CarouselSlick/v1/SlickSettings"
import {
  getManualSliderControlAnalytics,
  getManualSliderLinkAnalytics,
} from "./analytics"
import _get from "lodash/get"
import ProductCard from "@/components/productCard/v1/ProductCard"
import useCarouselSlicki18n from "@/i18n/useCarouselSlicki18n"
import { useSelector } from "react-redux"
import { selectAuthState } from "@/store/features/authSlice"

const ACCESSIBLE_FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])'
  
const ManualSlider = ({ data }) => {
  const [general, setGeneral] = useState()
  const { width } = useWindowSize()
  const slickRef = useRef(null)
  const sliderContainerRef = useRef(null)
  const shouldFocusActiveSlideRef = useRef(false)
  const [isTabOrMobile, setIsTabOrMobile] = useState(width < 1024)
  const headingId = useId()
  const { isAuth } = useSelector(selectAuthState)

  const [finalProducts, setFinalProducts] = useState([])
  const [activeSlideAnnouncement, setActiveSlideAnnouncement] = useState("")

  const { emptyCarouselListMsg } = useCarouselSlicki18n()
  const isManualSlider = ["manualProducts","dynamicProducts"].includes(data?.productType) || false

  useEffect(() => {
    const configFn = async () => {
      const { general } = await getConfig()
      setGeneral(general)
    }
    configFn()
  }, [])

  useEffect(() => {
    setIsTabOrMobile(width < 1024)
  }, [width])

  const eyeBrowText = data?.eyeBrowText
  const headerText = sanitizeText(data?.headline)
  const bodyText = sanitizeText(data?.description)
  const ctaText = isManualSlider ? data?.moduleCTAText : null
  const ctaUrl = isManualSlider  ? data?.moduleCtaUrl : null
  const newTab = data?.newTab || false
  const headerAlignment = isManualSlider ? "left" : "center"
  const showNavDots = data?.navDotsEnabled || false

  const productSliderItems = data?.products || []

  const dynamicProductSliderItems =
    data?.dynamicFeatureProductDetails?.productSkuId || []

  const sliderType = data?.products ? "manual" : "dynamic"
  const getShowArrowIndication = defaultValue =>
    data?.showArrowIndication === undefined
      ? defaultValue
      : data.showArrowIndication === true
  const showArrowIndication = getShowArrowIndication(false)
  const hasManualSliderItems = productSliderItems.length > 0
  const hasDynamicSliderConfig = dynamicProductSliderItems.length > 0
  const hasDynamicSliderItems = finalProducts.length > 0
  const totalSlides = hasManualSliderItems
    ? productSliderItems.length
    : finalProducts.length

  const sliderLabel = headerText || eyeBrowText || "Product slider"
  const getSlideNumber = slideIndex => {
    if (totalSlides === 0 || Number.isNaN(slideIndex)) {
      return null
    }

    return (((slideIndex % totalSlides) + totalSlides) % totalSlides) + 1
  }
  const getSlideAnnouncement = slideNumber =>
    slideNumber ? `Slide ${slideNumber} of ${totalSlides}` : ""
  const moduleCtaUrl = getShortenedUrlwithGeneral(ctaUrl, general)
  const moduleCtaAnalytics = getManualSliderLinkAnalytics({
    link: moduleCtaUrl,
    linkName: ctaText || "learn more",
    sliderTitle: sliderLabel,
  })

  useEffect(() => {
    if (dynamicProductSliderItems?.length > 0) {
      let skuids = dynamicProductSliderItems
      getConfig().then(async config => {
        if(Object.keys(config).length > 0){
          const language = _get(config, "internationalization.language", "en")
          const { general: { lwAppName = "" } = {} } = config ?? {}
          let data = await fetchFromSkuIds(skuids, lwAppName, language)

          if (data) {
            const productData = _get(data, "response.docs", {})
            let availableSkus = productData.map(product => product.sku_s)
            let availableSkus1 = skuids.filter(sku => availableSkus.includes(sku))
            let filteredItemOrder = availableSkus1.map(sku => productData.find(product => product.sku_s === sku))
            const productDataWithUrls = await Promise.all(
              filteredItemOrder.map(async item => {
                const pdpUrl = await getPDPUrl(
                  item?.Product_Category,
                  item?.slug_s
                )

                return {
                  ...item,
                  manualSliderPdpUrl: pdpUrl,
                }
              })
            )
            setFinalProducts(productDataWithUrls)
            if (slickRef) {
              slickRef.current?.slickGoTo(0)
            }
          }
        }
      })
    }
  }, [dynamicProductSliderItems,isAuth])
  const updateSlideAccessibility = () => {
    const sliderNode = sliderContainerRef.current

    if (!sliderNode) {
      return
    }

    const slides = sliderNode.querySelectorAll(".slick-slide")
    slides.forEach(slide => {
      const isActive = slide.classList.contains("slick-active")
      const slideNumber = getSlideNumber(
        Number(slide.getAttribute("data-index"))
      )

      slide.setAttribute("role", "group")
      slide.setAttribute("aria-roledescription", "slide")
      slide.setAttribute(
        "aria-label",
        getSlideAnnouncement(slideNumber) || sliderLabel
      )
      slide.setAttribute("aria-hidden", isActive ? "false" : "true")
      slide.setAttribute("aria-current", isActive ? "true" : "false")

      slide.querySelectorAll(ACCESSIBLE_FOCUSABLE_SELECTOR).forEach(element => {
        if (isActive) {
          element.removeAttribute("tabindex")
        } else {
          element.setAttribute("tabindex", "-1")
        }
      })
    })
  }

  const handleSlideFocus = event => {
    const slideNode = event.target.closest(".slick-slide")
    const slideIndex = Number(slideNode?.getAttribute("data-index"))

    if (
      !slideNode ||
      slideNode.classList.contains("slick-active") ||
      Number.isNaN(slideIndex)
    ) {
      return
    }

    slickRef.current?.slickGoTo(slideIndex)

    window.requestAnimationFrame(() => {
      event.target.focus()
    })
  }

  const focusFirstInteractiveElementInActiveSlide = () => {
    const sliderNode = sliderContainerRef.current

    if (!sliderNode) {
      return
    }

    const activeSlide = sliderNode.querySelector(".slick-slide.slick-active")
    const firstFocusableElement = activeSlide?.querySelector(
      ACCESSIBLE_FOCUSABLE_SELECTOR
    )

    if (firstFocusableElement instanceof HTMLElement) {
      firstFocusableElement.focus()
    } else if (activeSlide instanceof HTMLElement) {
      activeSlide.setAttribute("tabindex", "-1")
      activeSlide.focus()
    }
  }

  const handleSliderKeyDownCapture = event => {
    if (
      event.key !== "Enter" &&
      event.key !== " " &&
      event.key !== "Spacebar"
    ) {
      return
    }

    const paginationButton = event.target.closest(".slick-dots li button")

    if (paginationButton) {
      shouldFocusActiveSlideRef.current = true
    }
  }

  const settings = singleSliderSettings({ showArrowIndication })
  const slickSettings = {
    ...settings,
    accessibility: true,
    autoplay: false,
    autoplaySpeed: 5000,
    infinite: false,
    slidesToShow: Math.min(
      hasManualSliderItems ? productSliderItems.length : finalProducts.length,
      width >= 1600 ? 4 : width >= 1024 ? 3 : width >= 561 ? 2 : 1
    ),
    slidesToScroll: 1,
    arrows: showArrowIndication,
    prevArrow: settings.prevArrow
      ? React.cloneElement(settings.prevArrow, {
          label: "Previous slide",
        })
      : undefined,
    nextArrow: settings.nextArrow
      ? React.cloneElement(settings.nextArrow, {
          label: "Next slide",
        })
      : undefined,
    dots: showNavDots,
    centerMode: false,
    pauseOnFocus: true,
    pauseOnHover: true,
    appendDots: dots => (
      <ul className="slick-dots" aria-label={`${sliderLabel} pagination`}>
        {React.Children.map(dots, (dot, idx) => {
          const isActive =
            typeof dot?.props?.className === "string" &&
            dot.props.className.includes("slick-active")
          const child = dot?.props?.children
          const inner = React.isValidElement(child)
            ? React.cloneElement(child, {
              "aria-label": `Go to slide ${idx + 1}`,
              "aria-current": isActive ? "true" : "false",
              className: `${child.props.className || ""} gbh-data-layer`.trim(),
              "data-gbh-data-layer": getManualSliderControlAnalytics({
                sliderTitle: sliderLabel,
                controlName: `slide ${idx + 1}`,
              }),
                type: "button",
              })
            : child

          return React.cloneElement(dot, { children: inner })
        })}
      </ul>
    ),
    afterChange: currentSlide => {
      window.requestAnimationFrame(() => {
        setActiveSlideAnnouncement(
          getSlideAnnouncement(getSlideNumber(currentSlide))
        )
        updateSlideAccessibility()

        if (shouldFocusActiveSlideRef.current) {
          focusFirstInteractiveElementInActiveSlide()
          shouldFocusActiveSlideRef.current = false
        }
      })
    },
  }

  useEffect(() => {
    setActiveSlideAnnouncement(getSlideAnnouncement(totalSlides > 0 ? 1 : null))
  }, [totalSlides])

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined
    }
    const timer = window.setTimeout(updateSlideAccessibility, 0)
    return () => window.clearTimeout(timer)
  }, [productSliderItems.length, finalProducts.length, width, showNavDots])

  const getManualSliderItems = productSliderItems?.map((element, i) => {
    const cardTitle = sanitizeText(element.cardTitle)
    const cardDescription = sanitizeText(element.cardDescription)
    const imageAltText = element.imageAltText || cardTitle || `Slide ${i + 1}`
    const cardEyeBrowText = element.eyeBrowText || ""
    const cardCtaUrl = getShortenedUrlwithGeneral(element.cardCtaUrl, general)
    const imageCtaUrl = getShortenedUrlwithGeneral(element.imageCtaUrl, general)
    const imageLinkLabel = cardTitle
      ? `${cardTitle} image`
      : `Open slide ${i + 1}`
    const ctaLabel =
      element.cardCTAText && cardTitle
        ? `${sanitizeText(element.cardCTAText)}: ${cardTitle}`
        : sanitizeText(element.cardCTAText)
    const imageAnalytics = getManualSliderLinkAnalytics({
      link: imageCtaUrl,
      linkName: imageLinkLabel,
      sliderTitle: sliderLabel,
    })
    const ctaAnalytics = getManualSliderLinkAnalytics({
      link: cardCtaUrl,
      linkName:
        sanitizeText(element.cardCTAText) || cardTitle || `slide ${i + 1}`,
      sliderTitle: sliderLabel,
    })

    return (
      <article
        className="manual-slider-gallery-item-card"
        key={`${cardTitle || "manual-slide"}-${i}`}
        onFocusCapture={handleSlideFocus}
      >
        {typeof window !== "undefined" &&
          (element.imageCtaUrl ? (
            <Link
              className="manual-slider-image-link gbh-data-layer"
              href={imageCtaUrl}
              target={element.newTab ? "_blank" : "_self"}
              aria-label={imageLinkLabel}
              rel="noreferrer"
              data-gbh-data-layer={imageAnalytics}
            >
              <DynamicImage
                className="slider-image"
                imgUrl={element.image}
                alt={imageAltText}
                role="img"
              />
            </Link>
          ) : (
            <DynamicImage
              className="slider-image"
              imgUrl={element.image}
              alt={imageAltText}
              role="img"
            />
          ))}
        <div className="manual-slider-gallery-content">
          {element.subTitle ? (
            <p className="item-subtitle">{element.subTitle}</p>
          ) : (
            ""
          )}
          {cardEyeBrowText ? (
            <p className="item-eyeBrowText">{cardEyeBrowText}</p>
          ) : (
            ""
          )}
          {cardTitle ? <h3 className="item-cardtitle">{cardTitle}</h3> : ""}
          {cardDescription ? (
            <p className="item-description">{cardDescription}</p>
          ) : (
            ""
          )}
          {element.cardCTAText && element.cardCtaUrl ? (
            <div className="item-cta-url">
              <Link
                className="learn-more gbh-data-layer"
                href={cardCtaUrl}
                target={element.newTab ? "_blank" : "_self"}
                aria-label={ctaLabel}
                rel="noreferrer"
                data-gbh-data-layer={ctaAnalytics}
              >
                {sanitizeText(element.cardCTAText)}
              </Link>
            </div>
          ) : (
            ""
          )}
        </div>
      </article>
    )
  })
  const getDynamicSliderItems = finalProducts?.map((element, index) => {
    const productName = sanitizeText(element?.ProductBrandName_s) ||
      sanitizeText(element?.productName_s) ||
      sanitizeText(element?.title_s) ||
      `product ${index + 1}`
    const productAnalytics = getManualSliderLinkAnalytics({
      link: element?.manualSliderPdpUrl,
      linkName: productName,
      sliderTitle: sliderLabel,
    })

    return (
      <div key={element?.id ?? `cpm-carousel-${index}-${getRandom()}`}>
        <div>
          <ProductCard
            data={{
              ...element,
              type: "productSku",
              productSkuId: element?.sku_s,
              presetConfigs: data?.dynamicFeatureProductDetails?.presetConfigs,
              plpPresetConfigs:
                data?.dynamicFeatureProductDetails?.plpPresetConfigs,
            }}
            product={element}
            showEmptyPlaceholder={true}
            singleSku={true}
            analyticsData={productAnalytics}
            newTab={newTab}
          />
        </div>
      </div>
    )
  })
  const renderMobileCTA = (
    <div className="manual-slider-cta-mobile">
      <Link
        className="cmp-teaser__action-link gbh-secondary-button active manual-slider-cta-text gbh-data-layer"
        href={moduleCtaUrl}
        target={newTab ? "_blank" : "_self"}
        aria-label={ctaText}
        rel="noreferrer"
        data-gbh-data-layer={moduleCtaAnalytics}
      >
        {ctaText}
      </Link>
    </div>
  )

  return (
    <section
      className="manual-slider"
      role="region"
      aria-label={!headerText ? sliderLabel : undefined}
      aria-labelledby={headerText ? headingId : undefined}
      aria-roledescription="carousel"
      ref={sliderContainerRef}
    >
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {activeSlideAnnouncement}
      </span>
      <div className={`${sliderType}-slider-header`}>
        <div className={`${sliderType}-slider-header-${headerAlignment}`}>
          {eyeBrowText && (
            <div className={`${sliderType}-slider-eyebrow-text`}>
              {eyeBrowText}
            </div>
          )}
          {headerText && (
            <h2 className={`${sliderType}-slider-header-text`} id={headingId}>
              {headerText}
            </h2>
          )}
          {bodyText && (
            <div className={`${sliderType}-slider-body-text`}>{bodyText}</div>
          )}
        </div>
          {headerAlignment === "left" && !isTabOrMobile ? (
            <div className={`${sliderType}-slider-header-right`}>
              {ctaText && ctaUrl ? (
                <Link
                  className="cmp-teaser__action-link gbh-secondary-button  active manual-slider-cta-text gbh-data-layer"
                  href={moduleCtaUrl}
                  target={newTab ? "_blank" : "_self"}
                  aria-label={ctaText}
                  rel="noreferrer"
                  data-gbh-data-layer={moduleCtaAnalytics}
                >
                  {ctaText}
                </Link>
              ) : null}
            </div>
          ) : null}
        
      </div>
      {hasManualSliderItems && (
        <div className="manual-slider-content">
          <div className="manual-slider-items">
            <div onKeyDownCapture={handleSliderKeyDownCapture}>
              <>
                <Slider
                  key="manual-slider-gallery"
                  {...slickSettings}
                  ref={slickRef}
                >
                  {hasManualSliderItems ? getManualSliderItems : null}
                </Slider>
              </>
            </div>
          </div>
          {headerAlignment === "left" && ctaText && ctaUrl && isTabOrMobile ? renderMobileCTA : null}
        </div>
      )}
      {hasDynamicSliderConfig && (
        <div className="dynamic-slider-content">
          <div className="dynamic-slider-items">
            <div onKeyDownCapture={handleSliderKeyDownCapture}>
              <>
                {!hasDynamicSliderItems && (
                  <div className="cmp-carousel__description">
                    <p>{emptyCarouselListMsg}</p>
                  </div>
                )}
                {hasDynamicSliderItems && (
                  <Slider
                    key="dynamic-slider-gallery"
                    {...slickSettings}
                    ref={slickRef}
                  >
                    {getDynamicSliderItems}
                  </Slider>
                )}
              </>
            </div>
          </div>
          {headerAlignment === "left" && ctaText && ctaUrl && isTabOrMobile ? renderMobileCTA : null}
        </div>
      )}
    </section>
  )
}

export default ManualSlider
